// src/screens/EditProfileScreen.js

import React, { useEffect, useMemo, useRef, useState } from 'react';
import './EditProfileScreen.css';

import { uploadToSpaces } from '../utils/uploadToSpaces';
import { compressImage } from '../utils/compressImage';
import { useNavigate } from 'react-router-dom';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import placeholderProfile from '../assets/placeholder-profile.png';
import placeholderBanner from '../assets/placeholder-banner.png';
import editOverlayIcon from '../assets/editoverlay-icon.png';

import ProfileImageDrawer from '../components/popups/ProfileImageDrawer';
import { unequipBanner } from '../services/profileBannerApi';
import { ISO_COUNTRY_CODES } from '../constants/countryCodes';

import { canAccessStudio } from '../studio/studioAccess';

import {
  createStudioAnalyticsEvent,
  trackStudioEvent,
} from '../studio/avatar/services/studioAnalyticsApi';

const sanitizeLocationInput = (value) => {
  return value.replace(/[^\p{L}\p{N}\s,-]/gu, '').trim();
};

function EditProfileScreen({ user, onSave }) {
  const navigate = useNavigate();

  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const equippedBanner = user?.equippedProfileBanner?.imageUrl
    ? user.equippedProfileBanner
    : null;

  const [bannerRemoved, setBannerRemoved] = useState(false);
  const [profilePic, setProfilePic] = useState(user.profilePic || '');
  const [bio, setBio] = useState(user.bio || '');

  const [countryCode, setCountryCode] = useState('');
  const [countryName, setCountryName] = useState('');
  const [customCountry, setCustomCountry] = useState('');

  const [loading, setLoading] = useState(false);
  const [picUploading, setPicUploading] = useState(false);
  const [bannerRemoving, setBannerRemoving] = useState(false);

  // null, 'avatar', or 'banner'
  const [imageDrawer, setImageDrawer] = useState(null);
  const [drawerError, setDrawerError] = useState('');

  const activeBanner = bannerRemoved ? null : equippedBanner;
  const bannerImage = activeBanner?.imageUrl || placeholderBanner;

  const hasCustomPhoto = Boolean(
    profilePic && profilePic !== placeholderProfile
  );

  const isSpecialAvatar = Boolean(
    user.isAdmin ||
      (user.adminRole && user.adminRole !== 'none') ||
      user.isApp ||
      user.accountType === 'app'
  );

  const isBusy = loading || picUploading || bannerRemoving;

  const countries = useMemo(() => {
    const dn = new Intl.DisplayNames(['en'], {
      type: 'region',
    });

    const list = ISO_COUNTRY_CODES
      .map((code) => ({
        code,
        name: dn.of(code) || code,
      }))
      .filter((country) => {
        return country.name && country.name.length > 1;
      })
      .sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

    return [
      ...list,
      {
        code: 'OTHER',
        name: 'Other',
      },
    ];
  }, []);

  useEffect(() => {
    const lower = (user.location || '').toLowerCase().trim();

    if (!lower) return;

    const found = countries.find((country) => {
      return (
        country.code !== 'OTHER' &&
        country.name.toLowerCase() === lower
      );
    });

    if (found) {
      setCountryCode(found.code);
      setCountryName(found.name);
      setCustomCountry('');
    } else {
      setCountryCode('OTHER');
      setCountryName('');
      setCustomCountry(user.location || '');
    }
  }, [user.location, countries]);

  const resolveLocationString = () => {
    if (countryCode === 'OTHER') {
      return sanitizeLocationInput(customCountry);
    }

    return countryName || '';
  };

  const handleBack = () => {
    if (isBusy) return;

    navigate('/profile', { replace: true });
  };

  const openImageDrawer = (type) => {
    if (isBusy) return;

    setDrawerError('');
    setImageDrawer(type);
  };

  const closeImageDrawer = () => {
    if (bannerRemoving) return;

    setImageDrawer(null);
    setDrawerError('');
  };

  const handleTakePhoto = () => {
    if (isBusy) return;

    cameraInputRef.current?.click();
    setImageDrawer(null);
  };

  const handleChooseGallery = () => {
    if (isBusy) return;

    galleryInputRef.current?.click();
    setImageDrawer(null);
  };

  const handleChooseBanner = () => {
    if (isBusy) return;

    setImageDrawer(null);

    navigate('/profile/banners', {
      state: {
        from: '/edit-profile',
      },
    });
  };



  const handleCreateAvatar = () => {
    if (isBusy || !canAccessStudio(user)) return;

    void trackStudioEvent(
      createStudioAnalyticsEvent(
        'studio_entry_clicked',
        { entryPoint: 'edit_profile' }
      )
    );

    setImageDrawer(null);
    setDrawerError('');

    navigate('/studio/avatar', {
      replace: true,
      state: {
        studioEntryPoint: 'edit_profile',
      },
    });
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];

    // Allow choosing the same image again.
    event.target.value = '';

    if (!file || isBusy) return;

    setPicUploading(true);

    try {
      const compressed = await compressImage(file);

      const url = await uploadToSpaces(
        compressed,
        'profile-pics'
      );

      setProfilePic(url);
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload profile picture.');
    } finally {
      setPicUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (isBusy) return;

    if (imageDrawer === 'avatar') {
      // Saved to the account when the user taps Save.
      setProfilePic(placeholderProfile);
      setImageDrawer(null);
      return;
    }

    if (imageDrawer !== 'banner' || !activeBanner) return;

    setBannerRemoving(true);
    setDrawerError('');

    try {
      // Banner removal is applied immediately by the existing API.
      await unequipBanner();

      setBannerRemoved(true);

      onSave?.({
        ...user,
        equippedProfileBanner: null,
      });

      try {
        const username = String(user.username || '')
          .trim()
          .toLowerCase();

        if (username) {
          localStorage.removeItem(
            `viewedUserProfile:${username}:${username}`
          );
        }
      } catch (_) {}

      setImageDrawer(null);
    } catch (err) {
      console.error('Failed to remove banner:', err);

      setDrawerError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          'Could not remove banner. Please try again.'
      );
    } finally {
      setBannerRemoving(false);
    }
  };

  const handleSave = async () => {
    if (isBusy) return;

    setLoading(true);

    const updatedProfile = {
      username: user.username,
      name: user.name,
      profilePic,
      bio,
      location: resolveLocationString(),
      userType: user.userType || 'pioneer',
    };

    try {
      const res = await api.put(
        `/users/update/${user._id}`,
        updatedProfile
      );

      onSave?.(res.data.user);

      navigate(-1);
    } catch (err) {
      console.error(err);

      alert(
        err.response?.data?.error ||
          'Network error. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterMerchant = () => {
    navigate('/register-merchant', {
      replace: true,
    });
  };

  return (
    <div className="edit-profile-screen">
      <div className="edit-profile-header">
        <img
          src={backIcon}
          onClick={handleBack}
          alt="Back"
          role="button"
          tabIndex={isBusy ? -1 : 0}
          aria-disabled={isBusy}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleBack();
            }
          }}
        />

        <h2>Edit Profile</h2>
      </div>

      <div className="edit-profile-scroll">
        {/* Curved banner with the avatar centred over its lower edge. */}
        <div className="ep-visual-header">
          <button
            type="button"
            className="ep-banner-button"
            onClick={() => openImageDrawer('banner')}
            disabled={isBusy}
            aria-label="Change profile banner"
          >
            <img
              src={bannerImage}
              alt={
                activeBanner?.name ||
                'Default profile banner'
              }
              className="ep-banner-image"
            />

            <span
              className="ep-image-shade"
              aria-hidden="true"
            />

            <img
              src={editOverlayIcon}
              alt=""
              className="ep-camera-icon ep-banner-camera"
            />
          </button>

          <button
            type="button"
            className={`ep-avatar-button ${
              isSpecialAvatar ? 'ep-avatar-special' : ''
            }`}
            onClick={() => openImageDrawer('avatar')}
            disabled={isBusy}
            aria-label={
              picUploading
                ? 'Uploading profile photo'
                : 'Change profile photo'
            }
            aria-busy={picUploading}
          >
            <img
              src={profilePic || placeholderProfile}
              alt="Profile"
              className="ep-avatar-image"
            />

            <span
              className="ep-image-shade"
              aria-hidden="true"
            />

            {picUploading ? (
              <span
                className="ep-upload-spinner"
                aria-hidden="true"
              />
            ) : (
              <img
                src={editOverlayIcon}
                alt=""
                className="ep-camera-icon"
              />
            )}
          </button>
        </div>

        {/* Only avatars allow camera/gallery uploads. */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          hidden
          style={{ display: 'none' }}
        />

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={handleImageChange}
          hidden
          style={{ display: 'none' }}
        />

        <label htmlFor="edit-profile-bio">Bio</label>

        <textarea
          id="edit-profile-bio"
          value={bio}
          onChange={(event) => setBio(event.target.value)}
          rows={3}
          placeholder="Tell us about yourself..."
          disabled={loading}
        />

        <label htmlFor="edit-profile-country">Country</label>

        <select
          id="edit-profile-country"
          value={countryCode}
          onChange={(event) => {
            const code = event.target.value;

            setCountryCode(code);

            if (code === 'OTHER') {
              setCountryName('');
              setCustomCountry('');
            } else {
              const found = countries.find((country) => {
                return country.code === code;
              });

              setCountryName(found?.name || '');
              setCustomCountry('');
            }
          }}
          disabled={loading}
          required
        >
          <option value="" disabled>
            Select your country
          </option>

          {countries.map(({ code, name }) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>

        {countryCode === 'OTHER' && (
          <>
            <label htmlFor="edit-profile-custom-country">
              Your Country (type it)
            </label>

            <input
              id="edit-profile-custom-country"
              type="text"
              value={customCountry}
              onChange={(event) =>
                setCustomCountry(
                  sanitizeLocationInput(event.target.value)
                )
              }
              placeholder="Type your country"
              disabled={loading}
            />
          </>
        )}

        <div className="register-merchant-section">
          {user.userType === 'merchant' ? (
            user.status === 'suspended' ? (
              <>
                <p style={{ color: 'red' }}>
                  Your merchant account has been suspended.
                </p>

                <button
                  type="button"
                  onClick={() => navigate('/appeal')}
                  disabled={isBusy}
                >
                  Appeal Suspension
                </button>
              </>
            ) : user.isMerchant ? (
              <>
                <p>You are currently a Merchant.</p>

                <button
                  type="button"
                  onClick={() =>
                    navigate('/deregister-merchant')
                  }
                  disabled={isBusy}
                >
                  De-Register
                </button>
              </>
            ) : user.status === 'pending' ? (
              <p>
                Awaiting admin approval for your Merchant
                application.
              </p>
            ) : user.status === 'rejected' ? (
              <>
                <p>
                  Your merchant application was rejected.
                </p>

                <button
                  type="button"
                  onClick={handleRegisterMerchant}
                  disabled={isBusy}
                >
                  Re-Apply
                </button>
              </>
            ) : null
          ) : (
            <>
              <p>Want to sell on the Marketplace?</p>

              <button
                type="button"
                onClick={handleRegisterMerchant}
                disabled={isBusy}
              >
                Register as a Merchant
              </button>
            </>
          )}
        </div>
      </div>

      <div className="edit-profile-footer">
        <button
          type="button"
          onClick={handleBack}
          disabled={isBusy}
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={isBusy}
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>



      <ProfileImageDrawer
        user={user}
        isOpen={Boolean(imageDrawer)}
        type={imageDrawer || 'avatar'}
        canRemove={
          imageDrawer === 'banner'
            ? Boolean(activeBanner)
            : hasCustomPhoto
        }
        busy={bannerRemoving}
        error={drawerError}
        onClose={closeImageDrawer}
        onTakePhoto={handleTakePhoto}
        onChooseGallery={handleChooseGallery}
        onChooseStudio={handleChooseBanner}
        onCreateAvatar={handleCreateAvatar}
        onRemove={handleRemoveImage}
      />



    </div>
  );
}

export default EditProfileScreen;
